// AccountTypeDetailPage · dedicated landing page per tier (Classic, Premier, Elite, Social, Islamic).
import { useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Check, ArrowRight, Star, Crown, Sparkles, Users, ShieldCheck, Zap, Layers, Gauge, Banknote, Moon,
  BookOpen, Scale, Clock, AlertTriangle,
  Smartphone, Heart, Activity, Copy, Signal, MessageCircle,
} from "lucide-react";
import MarketingShell, { FeatureGrid, SectionHeading } from "@/components/MarketingShell";
import PelicanAppScreen from "@/components/PelicanAppScreen";
import InstitutionalSections from "@/components/InstitutionalSections";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useCtaTarget } from "@/lib/useCtaTarget";
import { REGISTER_URL, SIGNIN_URL } from "@/data/site";

// App-store links (verbatim from ofinancial.markets/social)

const TIER_META = {
  classic: { Icon: Star,     hero: "Classic",  artwork: "classic"  },
  premier: { Icon: Crown,    hero: "Premier",  artwork: "premier"  },
  elite:   { Icon: Sparkles, hero: "Elite",    artwork: "elite"    },
  social:  { Icon: Users,    hero: "Social",   artwork: "social"   },
  islamic: { Icon: Moon,     hero: "Islamic",  artwork: "islamic"  },
  cent:    { Icon: Banknote, hero: "Cent",     artwork: "cent"     },
};

// Islamic isn't in ACCOUNTS · it's a flag on the Classic tier. Mirror the Classic
// account specs but stamp the swap-free / halal blurb on top. Available on Classic only.
// `accounts` arg is the merged dict from useSiteSettings (admin overrides on top of
// the static defaults in @/data/site) so any admin edit propagates here too.
function getAccount(slug, accounts, t) {
  if (slug === "islamic") {
    return {
      ...accounts.classic,
      label: "Islamic",
      leverage: "1:500",
      blurb: t("accountDetail.islamic.blurb", "Sharia-compliant swap-free conditions on FX and Gold for up to two weeks per position. Zero overnight financing, zero hidden interest, low commission, full Mufti-aligned conditions."),
    };
  }
  const a = accounts[slug];
  if (!a) return a;
  // Override the (English-only) blurb sitting in /data/site.js with a
  // localized version. Admin overrides in the merged dict still win
  // because they replace the original blurb · we only swap when the
  // current blurb still matches the default English copy.
  const localized = t(`accountDetail.blurbs.${slug}`, "");
  return localized ? { ...a, blurb: localized } : a;
}

// Translation-aware data builders · evaluated per-render so locale switches reflow.
const getPerks = (t) => [
  { Icon: Zap,        title: t("accountDetail.perks.dma.title", "DMA & STP execution"),    body: t("accountDetail.perks.dma.body",    "Direct market access · your orders are routed straight to liquidity providers.") },
  { Icon: Layers,     title: t("accountDetail.perks.symbols.title", "1200+ instruments"),  body: t("accountDetail.perks.symbols.body","FX, metals, indices, energies, shares and crypto from a single MT5 terminal.") },
  { Icon: Gauge,      title: t("accountDetail.perks.leverage.title", "Dynamic leverage"),  body: t("accountDetail.perks.leverage.body","Leverage scales up as your account grows · efficient and safe.") },
  { Icon: ShieldCheck,title: t("accountDetail.perks.segregated.title", "Segregated funds"),body: t("accountDetail.perks.segregated.body","Client funds in tier-1 banks, fully separated from operating capital.") },
  { Icon: Banknote,   title: t("accountDetail.perks.withdrawals.title", "Fast withdrawals"),body: t("accountDetail.perks.withdrawals.body","Same-day processing across e-wallets, crypto and bank channels.") },
  { Icon: Sparkles,   title: t("accountDetail.perks.bonus.title", "50% deposit bonus"),    body: t("accountDetail.perks.bonus.body",   "First deposit bonus up to USD 5,000 on qualified accounts · subject to bonus terms.") },
];

// Islamic-specific Sharia principles
const getShariaPrinciples = (t) => [
  { Icon: Moon,       title: t("accountDetail.sharia.riba.title", "No Riba (interest)"),     body: t("accountDetail.sharia.riba.body", "Zero overnight swap or rollover fees. Positions held across midnight UTC do not accrue or pay any interest.") },
  { Icon: Scale,      title: t("accountDetail.sharia.gharar.title", "No Gharar (uncertainty)"),body: t("accountDetail.sharia.gharar.body","All trading terms — spread, commission, leverage, lot size — are disclosed upfront. No hidden contingencies.") },
  { Icon: Clock,      title: t("accountDetail.sharia.settle.title", "Immediate settlement"),  body: t("accountDetail.sharia.settle.body","Trades execute and settle in real time on MT5. No deferred delivery or speculative future contracts.") },
  { Icon: ShieldCheck,title: t("accountDetail.sharia.segregated.title", "Fully segregated"),  body: t("accountDetail.sharia.segregated.body","Client funds are held separately in tier-1 banks · never co-mingled with broker capital.") },
];

const getIslamicFaq = (t) => [
  { q: t("accountDetail.islamicFaq.cert.q",     "Is OFinancial Markets Sharia-certified?"),
    a: t("accountDetail.islamicFaq.cert.a",     "Our Islamic account conditions are designed in alignment with AAOIFI standards and major Mufti rulings on FX. The account is offered as a Hawala-style facility · no riba, no overnight financing, no hidden fees.") },
  { q: t("accountDetail.islamicFaq.midnight.q", "What happens if I hold a position past midnight UTC?"),
    a: t("accountDetail.islamicFaq.midnight.a", "On a regular account a swap (interest) would be debited or credited. On the Islamic account, swap = 0 for positions held up to two weeks. Beyond the two-week window, an administrative fee may apply — fully disclosed in advance.") },
  { q: t("accountDetail.islamicFaq.symbols.q",  "Which instruments are available swap-free?"),
    a: t("accountDetail.islamicFaq.symbols.a",  "Swap-free conditions cover Forex pairs and Gold (XAU/USD) only. Positions in all other asset classes — Indices, Energies, Shares and Crypto — accrue standard overnight financing.") },
  { q: t("accountDetail.islamicFaq.enable.q",   "How do I enable swap-free on my account?"),
    a: t("accountDetail.islamicFaq.enable.a",   "Request it during signup, or contact support after KYC approval. We will tag the account as swap-free typically within a few business hours · no fee, no minimum deposit upgrade required.") },
];

/* ===================== Social-only enrichment block ===================== */

// Theme-aware phone bezel · hoisted to module scope so React doesn't tear
// down + recreate the subtree on every SocialEnrichment render (eslint
// react/no-unstable-nested-components). Reads the theme itself so callers
// can drop it in without plumbing styles.
function PhoneBezel({ variant, accent = "var(--brand)", className = "" }) {
  const { theme } = useTheme();
  const isLight = theme === "light";
  const bezelGrad = isLight
    ? "linear-gradient(160deg, #f4f6f9 0%, #d6dae1 35%, #b9bfc8 70%, #8e95a0 100%)"
    : "linear-gradient(160deg, #25303f 0%, #131c28 35%, #0a121b 70%, #050a11 100%)";
  const screenBg  = isLight ? "#f7f9fc" : "#0a1118";
  const innerHi   = isLight ? "rgba(255,255,255,0.65)" : "rgba(255,255,255,0.05)";
  const innerLo   = isLight ? "rgba(0,0,0,0.18)" : "rgba(0,0,0,0.55)";
  const glassSheen = isLight
    ? "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, transparent 22%, transparent 78%, rgba(0,0,0,0.08) 100%)"
    : "linear-gradient(135deg, rgba(255,255,255,0.10) 0%, transparent 22%, transparent 78%, rgba(0,0,0,0.20) 100%)";
  return (
    <div
      className={`relative rounded-[34px] overflow-hidden mx-auto ${className}`}
      style={{
        width: "min(240px, 70vw)",
        aspectRatio: "1080 / 2200",
        background: bezelGrad,
        boxShadow: `0 28px 50px -20px color-mix(in srgb, var(--brand) 16%, rgba(0,0,0,0.55)),
                    0 14px 30px -10px rgba(0,0,0,0.40),
                    inset 0 1px 0 ${innerHi},
                    inset 0 -1px 0 ${innerLo}`,
      }}
    >
      <div
        className="absolute inset-[8px] rounded-[26px] overflow-hidden"
        style={{ background: screenBg, boxShadow: "inset 0 0 0 0.5px rgba(0,0,0,0.45)" }}
      >
        <PelicanAppScreen variant={variant} accent={accent} />
      </div>
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none z-40 rounded-[34px]"
        style={{ background: glassSheen }}
      />
    </div>
  );
}

function SocialEnrichment() {
  const { t } = useTranslation();
  const features = [
    { Icon: Copy,          title: t("accountDetail.social.features.copy.title", "Copy trading"),        body: t("accountDetail.social.features.copy.body", "Track, analyse and replicate strategies from experienced Signal owners directly from your Trader's Room.") },
    { Icon: Signal,        title: t("accountDetail.social.features.signal.title", "Become a Signal owner"), body: t("accountDetail.social.features.signal.body", "Share your expertise and earn copy-trading fees from followers who subscribe to your strategy.") },
    { Icon: Activity,      title: t("accountDetail.social.features.activity.title", "In-depth statistics"), body: t("accountDetail.social.features.activity.body", "Access a wide range of performance statistics for every Signal owner before you follow.") },
    { Icon: MessageCircle, title: t("accountDetail.social.features.messaging.title", "Multi-asset copying"), body: t("accountDetail.social.features.messaging.body", "Copy strategies across FX, metals, indices, commodities, stocks, ETFs and digital assets.") },
    { Icon: Users,         title: t("accountDetail.social.features.profiles.title", "Interactive profiles"), body: t("accountDetail.social.features.profiles.body", "View monthly returns, profits, markets traded, and more from easy-to-use trader profiles.") },
    { Icon: ShieldCheck,   title: t("accountDetail.social.features.risk.title", "Risk management"),     body: t("accountDetail.social.features.risk.body", "Control your copy-trading risk effectively with advanced max-drawdown and equity-stop tools.") },
  ];

  return (
    <>
      {/* ===== App download banner ===== */}
      <section
        id="social-app-banner"
        className="relative rounded-3xl border overflow-hidden mb-24"
        style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}
        data-testid="social-app-banner"
      >
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(120% 60% at 75% 20%, color-mix(in srgb, var(--brand) 16%, transparent), transparent 60%)" }}
        />
        <div className="relative grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-center p-7 sm:p-10 lg:p-14">
          {/* Left: copy + badges + downloads · centred on mobile, left on lg+ */}
          <div className="text-center lg:text-left">
            {/* Social Trading brand row · lives inside the OFinancial Trader's Room */}
            <div className="inline-flex flex-wrap items-center gap-3 mb-6 px-3 py-2 rounded-xl border" style={{ borderColor: "var(--line-strong)", background: "var(--surface-2)" }}>
              <div className="flex items-center gap-2.5">
                <img src="/brand/svg/logo.svg" alt="OFinancial" width={34} height={34} className="rounded-lg" />
                <div className="leading-tight text-left">
                  <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3">OFinancial</div>
                  <div className="font-display text-[14px] font-medium text-text-1">{t("accountDetail.social.socialBrand", "Social Trading")}</div>
                </div>
              </div>
              <div className="w-px h-7 mx-1" style={{ background: "var(--line-strong)" }} />
              <div className="flex items-center gap-2.5">
                <div className="w-[34px] h-[34px] rounded-lg grid place-items-center" style={{ background: "color-mix(in srgb, var(--brand) 14%, transparent)", color: "var(--brand)" }}><Users size={18} /></div>
                <div className="leading-tight text-left">
                  <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3">{t("accountDetail.social.insideThe", "Inside the")}</div>
                  <div className="font-display text-[14px] font-medium text-text-1">{t("accountDetail.social.tradersRoom", "Trader's Room")}</div>
                </div>
              </div>
            </div>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-[-0.025em] leading-[1.15] text-text-1 mb-4">
              {t("accountDetail.social.builtTitle", "The social trading platform")} <span className="text-gradient-brand font-semibold">{t("accountDetail.social.builtAccent", "built for you")}</span>
            </h2>
            <p className="text-text-3 text-[14.5px] leading-relaxed font-light max-w-prose mx-auto lg:mx-0 mb-6">
              {t("accountDetail.social.builtLeadPre", "Social Trading is built into your OFinancial Trader's Room, powered by")} <a href="https://www.fairtradingtech.com/brokertools/social-trading" target="_blank" rel="noopener noreferrer" className="text-text-1 font-medium hover:text-brand underline-offset-2 hover:underline">Fair Trading Technology</a>. {t("accountDetail.social.builtLeadPost", "Follow experienced Signal owners, copy their trades on your live MT5 account, and subscribe or unsubscribe at any time.")}
            </p>

            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3" data-testid="social-room-ctas">
              <a href={SIGNIN_URL} target="_blank" rel="noopener noreferrer" className="btn-solid-brand !h-12 !px-7 inline-flex" data-testid="social-open-room">
                {t("accountDetail.social.openRoom", "Open Social Trading in Trader's Room")} <ArrowRight size={15} className="rtl:rotate-180" />
              </a>
              <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer" className="btn-ghost-gradient !h-12 !px-7 inline-flex" data-testid="social-open-account">
                {t("accountDetail.social.openAccount", "Open a live account")}
              </a>
            </div>

            {/* Trust row · centered on mobile */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 mt-6 font-mono text-[12px] uppercase tracking-[0.16em] text-text-3">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} className="text-brand" /> {t("accountDetail.social.trust.mt5", "Built on live MT5 accounts")}</span>
              <span className="inline-flex items-center gap-1.5"><Activity size={12} className="text-brand" /> {t("accountDetail.social.trust.oneTap", "Subscribe & unsubscribe anytime")}</span>
              <span className="inline-flex items-center gap-1.5"><Users size={12} className="text-brand" /> {t("accountDetail.social.trust.community", "Global trader community")}</span>
            </div>
          </div>

          {/* Right: two-phone stack · VISIBLE ON MOBILE TOO */}
          <div className="relative flex items-center justify-center pt-2 lg:pt-0" style={{ perspective: "1500px" }}>
            <div className="relative" style={{ width: "min(360px, 90%)", height: "min(560px, 100vw)" }}>
              {/* Back phone · trader profile screen */}
              <div
                className="absolute left-0 top-6 hidden sm:block"
                style={{ transform: "rotateY(14deg) rotateZ(-4deg)", transformOrigin: "center" }}
              >
                <PhoneBezel variant="profile" />
              </div>
              {/* Front phone · performance dashboard screen · the hero shot */}
              <div
                className="absolute left-1/2 top-0 -translate-x-1/2"
                style={{ transform: "translateX(-50%) rotateY(-10deg) rotateZ(3deg)", transformOrigin: "center" }}
              >
                <PhoneBezel variant="performance" />
              </div>
              {/* Floating "+9,373% return" pill (lg only — keeps mobile clean) */}
              <div className="hidden lg:block absolute -right-2 bottom-10 px-3 py-2 rounded-xl backdrop-blur-md" style={{ background: "color-mix(in srgb, var(--surface) 92%, transparent)", border: "1px solid color-mix(in srgb, var(--brand) 25%, transparent)", boxShadow: "0 14px 40px -10px rgba(0,0,0,0.5)" }}>
                <div className="font-mono text-[8.5px] uppercase tracking-[0.22em] text-text-3 mb-0.5">{t("accountDetail.social.topTrader", "Top trader · 12m")}</div>
                <div className="flex items-baseline gap-1.5">
                  <span className="font-display text-xl font-semibold tabular-nums" style={{ color: "#54d97c" }}>+9,373%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 6-feature grid ===== */}
      <SectionHeading
        eyebrow={t("accountDetail.social.features.eyebrow", "What's inside")}
        title={t("accountDetail.social.features.title", "All-in-one")}
        accent={t("accountDetail.social.features.accent", "social trading")}
        lead={t("accountDetail.social.features.lead", "Log in to your Trader's Room, open Social Trading and start following Signal owners in seconds.")}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5 mb-24" data-testid="social-features-grid">
        {features.map(({ Icon, title, body }, i) => (
          <motion.div
            key={title}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: i * 0.05 }}
            className="rounded-2xl border p-6 group"
            style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}
          >
            <div className="w-11 h-11 rounded-xl grid place-items-center mb-4 transition-transform group-hover:scale-110 group-hover:-rotate-3" style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", border: "1px solid color-mix(in srgb, var(--brand) 28%, transparent)", color: "var(--brand)" }}>
              <Icon size={20} strokeWidth={1.7} />
            </div>
            <h3 className="font-display text-base font-medium text-text-1 mb-2 leading-tight">{title}</h3>
            <p className="text-text-3 text-[13px] leading-relaxed font-light">{body}</p>
          </motion.div>
        ))}
      </div>

      {/* ===== 3-step onboarding ===== */}
      <SectionHeading
        eyebrow={t("accountDetail.social.steps.eyebrow", "3 steps to get started")}
        title={t("accountDetail.social.steps.title", "From signup")}
        accent={t("accountDetail.social.steps.accent", "to social trading")}
        lead={t("accountDetail.social.steps.lead", "Open a live OFinancial Markets account, log in to the Trader's Room and start following Signal owners.")}
      />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-24" data-testid="social-steps">
        {[
          { n: "01", title: t("accountDetail.social.steps.s01.title", "Open a live account"),      body: t("accountDetail.social.steps.s01.body", "Register with your basic details and complete KYC by uploading proof of ID and residence.") },
          { n: "02", title: t("accountDetail.social.steps.s02.title", "Open Social Trading"),       body: t("accountDetail.social.steps.s02.body", "Log in to your Trader's Room and open the Social Trading section. Existing clients can skip straight to step 3.") },
          { n: "03", title: t("accountDetail.social.steps.s03.title", "Discover & copy traders"),   body: t("accountDetail.social.steps.s03.body", "Browse Signal owners, analyse their statistics and subscribe to copy their trades — unsubscribe at any time.") },
        ].map((s, i) => (
          <motion.div
            key={s.n}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: i * 0.06 }}
            className="rounded-2xl border p-6 relative overflow-hidden"
            style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}
          >
            <div className="font-mono text-[12px] uppercase tracking-[0.22em] mb-3" style={{ color: "var(--brand)" }}>{t("accountDetail.social.steps.stepLabel", "Step")} {s.n}</div>
            <h3 className="font-display text-lg font-medium text-text-1 mb-2 leading-tight">{s.title}</h3>
            <p className="text-text-3 text-[13.5px] leading-relaxed font-light">{s.body}</p>
          </motion.div>
        ))}
      </div>
    </>
  );
}
function IslamicEnrichment() {
  const { t } = useTranslation();
  const SHARIA_PRINCIPLES = getShariaPrinciples(t);
  const ISLAMIC_FAQ = getIslamicFaq(t);
  const ISLAMIC_TIERS = [
    { tier: t("accountDetail.islamicSection.matrix.tiers.classic", "Classic"),  available: true,  min: "$50" },
    { tier: t("accountDetail.islamicSection.matrix.tiers.premier", "Premier"),  available: false, min: "—"   },
    { tier: t("accountDetail.islamicSection.matrix.tiers.elite",   "Elite"),    available: false, min: "—"   },
    { tier: t("accountDetail.islamicSection.matrix.tiers.social",  "Social"),   available: false, min: "—"   },
  ];
  return (
    <>
      {/* ===== Sharia principles ===== */}
      <SectionHeading
        eyebrow={t("accountDetail.islamicSection.sharia.eyebrow", "Sharia principles")}
        title={t("accountDetail.islamicSection.sharia.title", "Swap-free trading,")}
        accent={t("accountDetail.islamicSection.sharia.accent", "properly engineered")}
        lead={t("accountDetail.islamicSection.sharia.lead", "Our swap-free accounts are built around four core Islamic finance principles · no interest, no uncertainty, immediate settlement, fully segregated.")}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-24" data-testid="sharia-principles">
        {SHARIA_PRINCIPLES.map(({ Icon, title, body }, i) => (
          <motion.div
            key={title}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.05 }}
            className="rounded-2xl border p-5 relative overflow-hidden group"
            style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}
          >
            {/* subtle crescent pattern */}
            <div
              aria-hidden
              className="absolute -top-6 -right-6 w-20 h-20 rounded-full opacity-[0.06] group-hover:opacity-[0.12] transition-opacity"
              style={{ background: "radial-gradient(circle, var(--brand) 0%, transparent 70%)" }}
            />
            <div className="relative w-11 h-11 rounded-xl grid place-items-center mb-4" style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", border: "1px solid color-mix(in srgb, var(--brand) 30%, transparent)", color: "var(--brand)" }}>
              <Icon size={20} strokeWidth={1.7} />
            </div>
            <h3 className="relative font-display text-base font-medium text-text-1 mb-2 leading-tight">{title}</h3>
            <p className="relative text-text-3 text-[13px] leading-relaxed font-light">{body}</p>
          </motion.div>
        ))}
      </div>

      {/* ===== Tier availability matrix ===== */}
      <SectionHeading
        eyebrow={t("accountDetail.islamicSection.matrix.eyebrow", "Tier availability")}
        title={t("accountDetail.islamicSection.matrix.title", "Swap-free on")}
        accent={t("accountDetail.islamicSection.matrix.accent", "every account that matters")}
        lead={t("accountDetail.islamicSection.matrix.lead", "Add swap-free to any of our three flagship accounts · same execution, same spreads, zero overnight cost.")}
      />
      <div className="rounded-2xl border overflow-hidden mb-24" style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }} data-testid="islamic-tier-matrix">
        <div className="hidden sm:grid grid-cols-12 gap-3 px-5 py-3 font-mono text-[12px] uppercase tracking-[0.18em] text-text-3 border-b" style={{ borderColor: "var(--line)" }}>
          <div className="col-span-4">{t("accountDetail.islamicSection.matrix.accountTier", "Account tier")}</div>
          <div className="col-span-4">{t("accountDetail.islamicSection.matrix.swapFree", "Swap-free")}</div>
          <div className="col-span-4">{t("accountDetail.islamicSection.matrix.minDeposit", "Min deposit")}</div>
        </div>
        <ul>
          {ISLAMIC_TIERS.map((row) => (
            <li key={row.tier} className="border-b last:border-b-0" style={{ borderColor: "var(--line)" }}>
              <div className="hidden sm:grid grid-cols-12 gap-3 px-5 py-4 items-center">
                <div className="col-span-4 text-text-1 text-[14px] font-medium">{row.tier}</div>
                <div className="col-span-4">
                  <span
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[12px] font-mono"
                    style={{
                      background: row.available ? "rgba(80,220,130,0.14)" : "rgba(240,106,106,0.10)",
                      color: row.available ? "#54d97c" : "#f06a6a",
                    }}
                  >
                    {row.available ? <Check size={12} strokeWidth={2.4} /> : <AlertTriangle size={12} strokeWidth={2.0} />}
                    {row.available ? t("accountDetail.islamicSection.matrix.available", "Available") : t("accountDetail.islamicSection.matrix.notAvailable", "Not available")}
                  </span>
                </div>
                <div className="col-span-4 font-mono text-[13px] text-text-2">{row.min}</div>
              </div>
              {/* mobile row */}
              <div className="grid sm:hidden grid-cols-12 gap-2 px-4 py-3 items-center">
                <div className="col-span-5 text-text-1 text-[14px] font-medium">{row.tier}</div>
                <div className="col-span-4 text-right">
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-mono"
                    style={{
                      background: row.available ? "rgba(80,220,130,0.14)" : "rgba(240,106,106,0.10)",
                      color: row.available ? "#54d97c" : "#f06a6a",
                    }}
                  >
                    {row.available ? <Check size={11} strokeWidth={2.4} /> : <AlertTriangle size={11} strokeWidth={2.0} />}
                    {row.available ? t("accountDetail.islamicSection.matrix.yes", "Yes") : t("accountDetail.islamicSection.matrix.no", "No")}
                  </span>
                </div>
                <div className="col-span-3 text-right font-mono text-[12px] text-text-2">{row.min}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* ===== Islamic FAQ ===== */}
      <SectionHeading
        eyebrow={t("accountDetail.islamicSection.faq.eyebrow", "Common questions")}
        title={t("accountDetail.islamicSection.faq.title", "Islamic account")}
        accent={t("accountDetail.islamicSection.faq.accent", "FAQ")}
        lead={t("accountDetail.islamicSection.faq.lead", "The most-asked questions from our Muslim trading community.")}
      />
      <div className="space-y-3 mb-24" data-testid="islamic-faq">
        {ISLAMIC_FAQ.map((row, i) => (
          <motion.details
            key={row.q}
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.35, delay: i * 0.04 }}
            className="group rounded-xl border px-5 py-4 cursor-pointer"
            style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}
          >
            <summary className="flex items-center justify-between gap-3 list-none">
              <span className="flex items-center gap-3 text-text-1 text-[14px] font-medium">
                <Moon size={15} className="text-brand shrink-0" strokeWidth={1.7} />
                {row.q}
              </span>
              <span className="text-brand text-[18px] font-light leading-none transition-transform group-open:rotate-45">+</span>
            </summary>
            <p className="text-text-3 text-[13px] leading-relaxed font-light mt-3 pl-7">{row.a}</p>
          </motion.details>
        ))}
      </div>
    </>
  );
}

export default function AccountTypeDetailPage() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const { getBadgeFor, accounts: ACCOUNTS_MERGED } = useSiteSettings();
  const detailCta = useCtaTarget("account_detail");
  const tier = getAccount(slug, ACCOUNTS_MERGED, t);
  if (!tier || !TIER_META[slug]) return <Navigate to="/account-comparison" replace />;
  const { Icon, hero, artwork } = TIER_META[slug];
  const isIslamic = slug === "islamic";
  const isSocial = slug === "social";

  const specs = [
    { label: t("accountDetail.specs.minDeposit", "Min Deposit"),       value: tier.minDeposit },
    { label: t("accountDetail.specs.floatingSpread", "Floating Spread"),   value: `${tier.spreadFrom} ${t("accountDetail.specs.pips", "pips")}` },
    { label: t("accountDetail.specs.commission", "Commission"),        value: `${tier.commission} ${t("accountDetail.specs.perSide", "per side")}` },
    { label: t("accountDetail.specs.maxLeverage", "Max Leverage"),      value: tier.leverage },
    { label: t("accountDetail.specs.execution", "Execution"),         value: tier.execution },
    { label: t("accountDetail.specs.baseCurrencies", "Base Currencies"),   value: tier.baseCurrencies.join(" · ") },
    { label: t("accountDetail.specs.minMaxLot", "Min / Max Lot"),     value: `${tier.minLot} / ${tier.maxLot}` },
    ...(isIslamic ? [{ label: t("accountDetail.specs.overnightSwap", "Overnight swap"), value: t("accountDetail.specs.overnightSwapValue", "0.00 — none") }] : []),
  ];

  return (
    <MarketingShell
      eyebrow={isIslamic ? t("accountDetail.shell.badge.sharia", "Sharia-compliant · Swap-free") : (isSocial ? t("accountDetail.shell.badge.pelican", "Social Trading · Trader's Room") : (getBadgeFor(slug, tier.badge) || t("accountDetail.shell.badge.accountType", "Account type")))}
      title={hero}
      titleAccent={isIslamic ? t("accountDetail.shell.titleAccent.trading", "trading") : (isSocial ? t("accountDetail.shell.titleAccent.trading", "trading") : t("accountDetail.shell.titleAccent.tradingAccount", "trading account"))}
      lead={tier.blurb}
      primary={{ label: isIslamic ? t("accountDetail.shell.cta.openSwapFree", "Open Swap-Free Account") : (isSocial ? t("accountDetail.shell.cta.openSocial", "Open Social Account") : t("accountDetail.shell.cta.openAccount", "Open Account")), href: detailCta.href, external: detailCta.isExternal }}
      secondary={isSocial ? { label: t("accountDetail.shell.cta.getApp", "How it works"), to: "#social-app-banner" } : { label: t("accountDetail.shell.cta.compareAll", "Compare all accounts"), to: "/account-comparison" }}
      artwork={artwork}
      testid={`account-type-${slug}`}
    >
      {/* For classic / premier / elite tiers we render the full
          institutional datasheet (live pricing · specifications ·
          execution quality · cost transparency · swap rates · personas ·
          side-by-side comparison · risk disclosure). The social and
          islamic tiers keep their existing bespoke layout because they
          have entirely different jobs-to-be-done. */}
      {!isIslamic && !isSocial && <InstitutionalSections tier={slug} />}

      {/* Legacy headline + specs section · ONLY rendered for the
          social and islamic tiers which still rely on this layout. */}
      {(isIslamic || isSocial) && (
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 mb-24">
        <div
          className="lg:col-span-5 rounded-2xl p-8 border relative overflow-hidden"
          style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}
        >
          {/* Islamic-themed crescent halo */}
          {isIslamic && (
            <div
              aria-hidden
              className="absolute -top-10 -right-10 w-48 h-48 rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, color-mix(in srgb, var(--brand) 22%, transparent) 0%, transparent 65%)" }}
            />
          )}
          <div className="relative w-14 h-14 rounded-xl grid place-items-center mb-5" style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", border: "1px solid color-mix(in srgb, var(--brand) 30%, transparent)", color: "var(--brand)" }}>
            <Icon size={22} strokeWidth={1.7} />
          </div>
          <div className="relative font-mono text-[12px] uppercase tracking-[0.22em] text-text-3 mb-2">
            {isIslamic ? t("accountDetail.islamic.overnightFinancing", "Overnight financing") : t("accountDetail.specs.minimumDeposit", "Minimum Deposit")}
          </div>
          <div className="relative font-display text-6xl font-medium text-text-1 tabular-nums tracking-[-0.025em]">
            {isIslamic ? "$0" : tier.minDeposit}
          </div>
          {isIslamic && <div className="relative text-text-3 text-[12px] font-mono mt-1 uppercase tracking-[0.18em]">{t("accountDetail.islamic.zeroSwap", "Zero swap · Zero riba")}</div>}
          <p className="relative text-text-3 text-[14px] mt-5 leading-relaxed font-light">{tier.blurb}</p>

          {/* Islamic-only · quick verification facts to balance the right specs column */}
          {isIslamic && (
            <div className="relative mt-6 pt-6 border-t" style={{ borderColor: "var(--line)" }} data-testid="islamic-quick-facts">
              <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3 mb-3">{t("accountDetail.islamic.quickVerification", "Quick verification")}</div>
              <ul className="space-y-2.5">
                {[
                  { Icon: Check,        text: t("accountDetail.islamic.facts.aaoifi",   "AAOIFI-aligned trading conditions") },
                  { Icon: Check,        text: t("accountDetail.islamic.facts.mufti",    "Approved by major Mufti rulings on FX") },
                  { Icon: Check,        text: t("accountDetail.islamic.facts.swapFree", "Swap-free on FX & Gold for up to 2 weeks per position") },
                  { Icon: Check,        text: t("accountDetail.islamic.facts.noFee",    "No fee for switching to swap-free at any time") },
                  { Icon: Check,        text: t("accountDetail.islamic.facts.maxLev",   "Max leverage 1:500 on Islamic accounts") },
                ].map(({ Icon, text }) => (
                  <li key={text} className="flex items-start gap-2.5 text-[13px] text-text-2 leading-snug">
                    <span className="shrink-0 w-4 h-4 mt-0.5 rounded-full inline-flex items-center justify-center" style={{ background: "color-mix(in srgb, var(--brand) 18%, transparent)", color: "var(--brand)" }}>
                      <Icon size={10} strokeWidth={3} />
                    </span>
                    <span className="font-light">{text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="lg:col-span-7 rounded-2xl p-8 border" style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}>
          <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3 mb-5">{t("accountDetail.specs.tradingConditions", "Trading Conditions")}</div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
            {specs.map(({ label, value }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.03 }}
                className="flex items-baseline justify-between gap-3 border-b border-line pb-3 min-w-0"
              >
                <dt className="text-text-3 text-[12px] uppercase tracking-wider font-mono shrink-0">{label}</dt>
                <dd className="text-text-1 text-[14px] font-medium tabular-nums text-right break-words">{value}</dd>
              </motion.div>
            ))}
          </dl>

          {/* Islamic-only · concrete "what does this save you" calculation */}
          {isIslamic && (
            <div className="mt-8 pt-6 border-t" style={{ borderColor: "var(--line)" }} data-testid="islamic-savings">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-7 h-7 rounded-md grid place-items-center" style={{ background: "color-mix(in srgb, var(--brand) 14%, transparent)", color: "var(--brand)" }}>
                  <BookOpen size={13} strokeWidth={1.8} />
                </span>
                <span className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3">{t("accountDetail.islamic.whatYouSave", "What you save · 1 lot EURUSD long")}</span>
              </div>
              <div className="grid grid-cols-3 gap-2.5">
                {[
                  { label: t("accountDetail.islamic.rows.perNight", "Per night"), standard: "$2.85", saving: "$2.85" },
                  { label: t("accountDetail.islamic.rows.perWeek",  "Per week"),  standard: "$22.80", saving: "$22.80" },
                  { label: t("accountDetail.islamic.rows.perMonth", "Per month"), standard: "$85.50", saving: "$85.50" },
                ].map((row) => (
                  <div key={row.label} className="rounded-xl border p-3" style={{ borderColor: "color-mix(in srgb, var(--brand) 20%, transparent)", background: "color-mix(in srgb, var(--brand) 4%, transparent)" }}>
                    <div className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-text-3 mb-1.5">{row.label}</div>
                    <div className="flex items-baseline justify-between gap-1">
                      <span className="font-mono text-[12px] text-text-3 line-through tabular-nums">{row.standard}</span>
                      <span className="font-display text-base font-medium tabular-nums" style={{ color: "var(--brand)" }}>$0</span>
                    </div>
                    <div className="font-mono text-[9.5px] text-[#54d97c] mt-1 tabular-nums">{t("accountDetail.islamic.saved", "saved")} {row.saving}</div>
                  </div>
                ))}
              </div>
              <p className="text-text-3 text-[12px] mt-3 font-light leading-snug">
                {t("accountDetail.islamic.savingsNote", "Based on the current illustrative EURUSD long swap of −$2.85 per lot per night on a Classic account. Held continuously across 30 calendar days. Actual swap rates change daily.")}
              </p>
            </div>
          )}
        </div>
      </section>
      )}

      {/* Islamic-only enrichment */}
      {isIslamic && <IslamicEnrichment />}

      {/* Social-only enrichment · Trader's Room Social Trading + features */}
      {isSocial && <SocialEnrichment />}

      {/* Legacy perks grid · only for social/islamic (classic/premier/elite
          get the institutional sections above which already cover this). */}
      {(isIslamic || isSocial) && (
        <>
          <SectionHeading
            eyebrow={t("accountDetail.legacyPerks.eyebrow", "What you get")}
            title={t("accountDetail.legacyPerks.title", "Why traders choose")}
            accent={tier.label}
            lead={t("accountDetail.legacyPerks.lead", "Every account type ships with our institutional-grade execution stack and full client support.")}
          />
          <FeatureGrid
            items={(isSocial || isIslamic)
              ? getPerks(t).filter(p => p.title !== t("accountDetail.perks.bonus.title", "50% deposit bonus"))
              : getPerks(t)
            }
            testid="account-perks-grid"
          />
        </>
      )}
    </MarketingShell>
  );
}
